import React, { useState } from "react";
import { ContentBlock } from "@/types/case-study";

interface CarouselBlockProps {
  block: ContentBlock;
}

export const CarouselBlock: React.FC<CarouselBlockProps> = ({ block }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = () => {
    const images = block.content?.images || [];
    setCurrentSlide((prev) => (prev + 1) % images.length);
  };

  const prevSlide = () => {
    const images = block.content?.images || [];
    setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const images = block.content?.images || [];
  const currentImage = images[currentSlide];

  // Store carousel navigation in window for zoom modal access
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__carouselNavigation = {
        currentSlide,
        totalSlides: images.length,
        nextSlide,
        prevSlide,
      };
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).__carouselNavigation;
      }
    };
  }, [currentSlide, images.length]);

  return (
    <div className="space-y-4">
      {/* Main carousel display */}
      <div className="relative">
        <div className="bg-gray-100 rounded-lg overflow-hidden relative flex items-center justify-center p-2 md:p-6">
          {currentImage && (
            <img
              src={currentImage.url}
              alt={currentImage.caption || `Carousel image ${currentSlide + 1}`}
              className="w-auto h-auto max-w-full max-h-[450px] object-contain"
              data-carousel-image="true"
              data-carousel-index={currentSlide}
              data-carousel-total={images.length}
            />
          )}
          
          {/* Navigation arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 text-white rounded-full flex items-center justify-center hover:bg-black/70 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 text-white rounded-full flex items-center justify-center hover:bg-black/70 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
        </div>
        
        {/* Slide counter */}
        {images.length > 1 && (
          <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
            {currentSlide + 1} / {images.length}
          </div>
        )}
      </div>

      {/* Caption display */}
      {currentImage?.caption && (
        <p className="text-sm text-gray-600 text-center italic px-4">
          {currentImage.caption}
        </p>
      )}

      {/* Thumbnail navigation */}
      <div className="flex gap-2 overflow-x-auto pb-2 justify-center">
        {images.map((image, index) => (
          <button
            key={image.id}
            onClick={() => goToSlide(index)}
            className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${
              index === currentSlide 
                ? 'border-orange-500' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <img
              src={image.url}
              alt={`Thumbnail ${index + 1}`}
              className="w-full h-full object-cover"
            />
          </button>
        ))}
      </div>
    </div>
  );
};